import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../cart/CartContext';
import { useWishlist } from '../wishlist/WishlistContext';
import { useSiteSettings } from '../SiteSettingsContext';

const DISMISS_KEY = 'saloni_wishlist_nudge_dismissed';

function readDismissed() {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

function writeDismissed() {
  try {
    sessionStorage.setItem(DISMISS_KEY, '1');
  } catch {
    /* ignore */
  }
}

/**
 * Prompt shoppers to revisit saved wishlist items while checking out.
 */
export default function WishlistCartNudge({ placement = 'cart' }) {
  const { items: wishlistItems } = useWishlist();
  const { lines } = useCart();
  const { settings: site } = useSiteSettings();
  const store = site.store_copy || {};
  const [dismissed, setDismissed] = useState(readDismissed);

  const savedNotInBag = useMemo(() => {
    if (!wishlistItems.length) return 0;
    const inBag = new Set(
      lines.map((line) => `${String(line.categorySlug || '').trim()}::${String(line.productId || '').trim()}`)
    );
    return wishlistItems.filter(
      (item) => !inBag.has(`${String(item.categorySlug || '').trim()}::${String(item.productId || '').trim()}`)
    ).length;
  }, [wishlistItems, lines]);

  if (dismissed || savedNotInBag === 0) return null;

  const countLabel = savedNotInBag === 1 ? '1 saved item' : `${savedNotInBag} saved items`;
  const title =
    placement === 'checkout'
      ? store.wishlist_nudge_checkout_title || 'Still deciding?'
      : store.wishlist_nudge_cart_title || 'You have saved pieces';
  const template =
    placement === 'checkout'
      ? store.wishlist_nudge_checkout_text ||
        '{count} in your wishlist — add them to this order or keep browsing before you pay.'
      : store.wishlist_nudge_cart_text ||
        '{count} waiting in your wishlist. Open saved items to add them to your bag.';
  const text = String(template).replace(/\{count\}/g, countLabel);

  return (
    <div className="wishlist-banner wishlist-banner--sync wishlist-cart-nudge" role="status">
      <div className="wishlist-banner__content">
        <p className="wishlist-banner__title">{title}</p>
        <p className="wishlist-banner__text mb-0">{text}</p>
      </div>
      <div className="wishlist-banner__actions">
        <Link to="/wishlist" className="btn btn-primary btn-sm rounded-pill px-3">
          View saved items
        </Link>
        <button
          type="button"
          className="btn btn-link btn-sm text-muted p-0"
          onClick={() => {
            writeDismissed();
            setDismissed(true);
          }}
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}
